import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import type { JSX, ReactNode } from 'react';
import { PillButton } from '@/components/entity/PillButton';

interface FilterModalProps {
  title: string;
  description?: string;
  trigger?: ReactNode;
  children: ReactNode;
  activeFilterLabel?: string;
  hasActiveFilters?: boolean;
  onReset?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FilterModal({
  title,
  description,
  trigger,
  children,
  activeFilterLabel,
  hasActiveFilters = false,
  onReset,
  open,
  onOpenChange,
}: FilterModalProps): JSX.Element {
  const hasTrigger = trigger !== null && trigger !== undefined;
  const isOpen = Boolean(open);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      {hasTrigger ? <Dialog.Trigger asChild>{trigger}</Dialog.Trigger> : null}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={clsx(
            'fixed inset-x-0 top-[4%] z-50 mx-auto flex max-h-[92vh] max-w-4xl flex-col gap-4',
            'rounded-3xl border border-surface-subtle bg-white px-5 py-4 shadow-2xl focus:outline-none',
            'md:top-[6%]',
            'relative',
          )}
          aria-label={title}
        >
          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full border border-surface-subtle text-text-primary/70 transition hover:border-brand-primary/50 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 md:h-10 md:w-10"
              aria-label="Close filters"
            >
              <Cross2Icon className="h-4 w-4" aria-hidden />
            </button>
          </Dialog.Close>
          <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-xl font-semibold text-text-primary">
                {title}
              </Dialog.Title>
              {typeof description === 'string' && description.length > 0 ? (
                <Dialog.Description className="text-sm text-text-primary/70">
                  {description}
                </Dialog.Description>
              ) : null}
              {typeof activeFilterLabel === 'string' && activeFilterLabel.length > 0 ? (
                <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                  {activeFilterLabel}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {onReset ? (
                <PillButton
                  variant="secondary"
                  size="sm"
                  disabled={!hasActiveFilters}
                  onClick={onReset}
                >
                  Reset
                </PillButton>
              ) : null}
            </div>
          </header>
          <div className="flex-1 overflow-y-auto pr-1">{children}</div>
          <div className="flex items-center justify-end gap-2 border-t border-surface-subtle pt-3">
            <Dialog.Close asChild>
              <PillButton variant="secondary" size="sm">
                Cancel
              </PillButton>
            </Dialog.Close>
            <Dialog.Close asChild>
              <PillButton size="sm">Apply filters</PillButton>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
